import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2,
  Save,
  Edit2,
  X,
  GripVertical,
  Trophy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SEASON_PARTS = [
  { name: "Field Season (Autumn)", order: 0 },
  { name: "Indoor Season (Winter)", order: 1 },
  { name: "Field Season (Spring)", order: 2 },
];

export const SeasonHome = () => {
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [otherSeasons, setOtherSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [seasonParts, setSeasonParts] = useState([]);
  const [showNewSeasonForm, setShowNewSeasonForm] = useState(false);
  const [newSeasonData, setNewSeasonData] = useState({
    club_name: "",
    team_name: "",
    title: ""
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchSeason();
  }, []);

  const fetchSeason = async () => {
    try {
      const response = await axios.get(`${API}/seasons`);
      if (response.data.length > 0) {
        const currentSeason = response.data[0];
        setSeason(currentSeason);
        setEditData({
          club_name: currentSeason.club_name,
          team_name: currentSeason.team_name,
          title: currentSeason.title
        });
        setSeasonParts(currentSeason.season_parts || []);
        setOtherSeasons(response.data.slice(1));
      } else {
        setOtherSeasons([]);
      }
    } catch (error) {
      toast.error("Seizoen laden mislukt");
    } finally {
      setLoading(false);
    }
  };

  const handleActivateSeason = async (targetSeasonId) => {
    try {
      await axios.post(`${API}/seasons/${targetSeasonId}/activate`);
      await fetchSeason();
      toast.success("Seizoen geactiveerd");
    } catch {
      toast.error("Seizoen wisselen mislukt");
    }
  };

  const handleTeamNameChange = (value) => {
    setNewSeasonData(prev => ({
      ...prev,
      team_name: value,
      title: value ? `Season ${value} ${new Date().getFullYear()}-${new Date().getFullYear() + 1}` : ""
    }));
  };

  const handleCreateSeason = async () => {
    if (!newSeasonData.club_name || !newSeasonData.team_name) {
      toast.error("Vul clubnaam en teamnaam in");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/seasons`, {
        ...newSeasonData,
        season_parts: DEFAULT_SEASON_PARTS
      });
      setSeason(response.data);
      setEditData({
        club_name: response.data.club_name,
        team_name: response.data.team_name,
        title: response.data.title
      });
      setSeasonParts(response.data.season_parts || []);
      setShowNewSeasonForm(false);
      setNewSeasonData({ club_name: "", team_name: "", title: "" });
      toast.success("Seizoen succesvol aangemaakt!");
    } catch (error) {
      toast.error("Seizoen aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSeasonDetails = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/seasons/${season.id}`, {
        ...editData,
        season_parts: seasonParts
      });
      setSeason(response.data);
      setEditing(false);
      toast.success("Seizoen bijgewerkt");
    } catch (error) {
      toast.error("Seizoen bijwerken mislukt");
    } finally {
      setSaving(false);
    }
  };

  const handleAddPart = () => {
    const newPart = {
      id: `new-${Date.now()}`,
      name: `New Part ${seasonParts.length + 1}`,
      order: seasonParts.length,
      isNew: true
    };
    setSeasonParts([...seasonParts, newPart]);
  };

  const handleRemovePart = (index) => {
    if (seasonParts.length <= 1) {
      toast.error("Je hebt minstens één seizoensdeel nodig");
      return;
    }
    const updated = seasonParts.filter((_, i) => i !== index);
    setSeasonParts(updated.map((p, i) => ({ ...p, order: i })));
  };

  const handlePartNameChange = (index, value) => {
    const updated = [...seasonParts];
    updated[index].name = value;
    setSeasonParts(updated);
  };

  const handleDeleteSeason = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/seasons/${deleteConfirm}`);
      setSeason(null);
      setSeasonParts([]);
      toast.success("Seizoen verwijderd");
    } catch (error) {
      toast.error("Seizoen verwijderen mislukt");
    } finally {
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  // No season exists - show create form
  if (!season) {
    return (
      <div data-testid="season-page" className="max-w-2xl mx-auto space-y-8">
        <div>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Season Instellingen
          </h1>
          <p className="text-slate-500 mt-2">Maak je eerste seizoen aan om te beginnen</p>
        </div>

        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle 
              className="text-lg font-semibold uppercase tracking-tight flex items-center gap-2"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              <Trophy className="w-5 h-5" />
              Nieuw Seizoen Aanmaken
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="club_name">Clubnaam</Label>
                <Input
                  id="club_name"
                  placeholder="bijv. HC Naarden"
                  value={newSeasonData.club_name}
                  onChange={(e) => setNewSeasonData({ ...newSeasonData, club_name: e.target.value })}
                  data-testid="new-club-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="team_name">Teamnaam</Label>
                <Input
                  id="team_name"
                  placeholder="bijv. JO14-5"
                  value={newSeasonData.team_name}
                  onChange={(e) => handleTeamNameChange(e.target.value)}
                  data-testid="new-team-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Seizoenstitel</Label>
              <Input
                id="title"
                placeholder="Automatisch gegenereerd"
                value={newSeasonData.title}
                onChange={(e) => setNewSeasonData({ ...newSeasonData, title: e.target.value })}
                data-testid="new-season-title-input"
              />
            </div>
            <Button
              onClick={handleCreateSeason}
              disabled={saving}
              className="w-full bg-slate-900 hover:bg-slate-800"
              data-testid="create-season-btn"
            >
              {saving ? "Aanmaken..." : "Seizoen aanmaken"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="season-page" className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Seizoen Instellingen
          </h1>
          <p className="text-slate-500 mt-2">Beheer je seizoen en seizoensdelen</p>
        </div>
        <div className="flex gap-2">
          {!editing && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowNewSeasonForm(true)}
                data-testid="new-season-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nieuw seizoen
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditing(true)}
                data-testid="edit-season-btn"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Bewerken
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Season Details Card */}
      <Card className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle 
            className="text-lg font-semibold uppercase tracking-tight"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Seizoen Details
          </CardTitle>
          {editing && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditing(false);
                  setEditData({
                    club_name: season.club_name,
                    team_name: season.team_name,
                    title: season.title
                  });
                  setSeasonParts(season.season_parts || []);
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Annuleren
              </Button>
              <Button
                size="sm"
                onClick={handleSaveSeasonDetails}
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="save-season-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                Opslaan
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {editing ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Clubnaam</Label>
                  <Input
                    value={editData.club_name}
                    onChange={(e) => setEditData({ ...editData, club_name: e.target.value })}
                    data-testid="edit-club-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teamnaam</Label>
                  <Input
                    value={editData.team_name}
                    onChange={(e) => setEditData({ ...editData, team_name: e.target.value })}
                    data-testid="edit-team-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Seizoenstitel</Label>
                <Input
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  data-testid="edit-title-input"
                />
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Club</p>
                <p className="text-lg font-semibold text-slate-900 mt-1">{season.club_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Team</p>
                <p className="text-lg font-semibold text-slate-900 mt-1">{season.team_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Seizoen</p>
                <p className="text-lg font-semibold text-slate-900 mt-1">{season.title}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Season Parts Card */}
      <Card className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle 
            className="text-lg font-semibold uppercase tracking-tight"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Seizoensdelen
          </CardTitle>
          {editing && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddPart}
              data-testid="add-part-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Deel toevoegen
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-3">
            {seasonParts.map((part, index) => (
              <div 
                key={part.id}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 group"
                data-testid={`season-part-${index}`}
              >
                <GripVertical className="w-4 h-4 text-slate-300" />
                <span className="w-8 h-8 bg-white border border-slate-200 rounded-md flex items-center justify-center text-sm font-medium text-slate-500">
                  {index + 1}
                </span>
                {editing ? (
                  <Input
                    value={part.name}
                    onChange={(e) => handlePartNameChange(index, e.target.value)}
                    className="flex-1 h-10 bg-white"
                    data-testid={`part-name-input-${index}`}
                  />
                ) : (
                  <span className="flex-1 text-slate-700">{part.name}</span>
                )}
                {editing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemovePart(index)}
                    className="h-8 w-8 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-part-btn-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Other seasons — allow switching without data loss */}
      {otherSeasons.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="other-seasons-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle
              className="text-lg font-semibold uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Andere Seizoenen ({otherSeasons.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-3">
            {otherSeasons.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
                data-testid={`other-season-${s.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{s.club_name} · {s.team_name}</p>
                  <p className="text-xs text-slate-500 truncate">{s.title}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleActivateSeason(s.id)}
                  data-testid={`activate-season-${s.id}`}
                >
                  Wissel naar dit seizoen
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Danger Zone */}
      <Card className="bg-white border border-red-200 rounded-xl shadow-sm">
        <CardHeader className="border-b border-red-100">
          <CardTitle 
            className="text-lg font-semibold uppercase tracking-tight text-red-600"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Gevarenzone
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Seizoen verwijderen</p>
              <p className="text-sm text-slate-500">Dit verwijdert permanent alle gegevens inclusief wedstrijden en planning</p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setDeleteConfirm(season.id)}
              data-testid="delete-season-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Verwijderen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Seizoen verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dit verwijdert permanent het seizoen, alle wedstrijden, spelers en planningsgegevens. Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSeason}
              className="bg-red-500 hover:bg-red-600"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Season Dialog */}
      <Dialog open={showNewSeasonForm} onOpenChange={setShowNewSeasonForm}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              Nieuw Seizoen Aanmaken
            </DialogTitle>
            <DialogDescription>
              Je huidige seizoen blijft bewaard. Het nieuwe seizoen wordt actief; je kunt later terugwisselen via de lijst onderaan deze pagina.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dialog-club-name">Clubnaam</Label>
                <Input
                  id="dialog-club-name"
                  placeholder="bijv. HC Naarden"
                  value={newSeasonData.club_name}
                  onChange={(e) => setNewSeasonData({ ...newSeasonData, club_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dialog-team-name">Teamnaam</Label>
                <Input
                  id="dialog-team-name"
                  placeholder="bijv. JO14-5"
                  value={newSeasonData.team_name}
                  onChange={(e) => handleTeamNameChange(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dialog-title">Seizoenstitel</Label>
              <Input
                id="dialog-title"
                placeholder="Automatisch gegenereerd"
                value={newSeasonData.title}
                onChange={(e) => setNewSeasonData({ ...newSeasonData, title: e.target.value })}
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewSeasonForm(false);
                  setNewSeasonData({ club_name: "", team_name: "", title: "" });
                }}
                className="flex-1"
              >
                Annuleren
              </Button>
              <Button
                onClick={async () => {
                  if (!newSeasonData.club_name || !newSeasonData.team_name) {
                    toast.error("Vul clubnaam en teamnaam in");
                    return;
                  }
                  await handleCreateSeason();
                  await fetchSeason();
                }}
                disabled={saving}
                className="flex-1 bg-slate-900 hover:bg-slate-800"
              >
                {saving ? "Aanmaken..." : "Seizoen aanmaken"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
